/**
 * Page Content Storage Service
 * Handles persistence of page content (BlockNote blocks). F 144: remote-backed,
 * with localStorage kept as an offline mirror.
 */

// F 144 — reads/writes go through RemoteState: an in-memory cache hydrated
// from the FCC state service and flushed back on a debounce. The localStorage
// mirror is kept in step, so behaviour is identical if the service is
// unreachable, and the signatures here stay SYNCHRONOUS.
import { RemoteState } from '../storage/RemoteState';
import { readContentEnvelope, writeContentEnvelope } from './pageContentFormat';
import { serializeBlocksToMarkdown, type DropReason } from '../markdown/serialize-blocks';

const CONTENT_KEY_PREFIX = 'freshcards:content:';

/**
 * Template content for new pages so they aren't blank.
 * Each template is an array of BlockNote blocks matching the
 * default schema (heading-2, paragraph, bullet-list-item, etc.).
 */
const TEMPLATE_CONTENT: Record<string, any[]> = {
  blank: [],
  meeting: [
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Meeting details', styles: {} }] },
    { type: 'paragraph', content: [{ type: 'text', text: 'Date: ', styles: { bold: true } }, { type: 'text', text: new Date().toLocaleDateString() }] },
    { type: 'paragraph', content: [{ type: 'text', text: 'Attendees: ', styles: { bold: true } }] },
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Agenda', styles: {} }] },
    { type: 'bulletListItem', content: [{ type: 'text', text: 'Topic 1' }] },
    { type: 'bulletListItem', content: [{ type: 'text', text: 'Topic 2' }] },
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Notes', styles: {} }] },
    { type: 'paragraph', content: [] },
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Action items', styles: {} }] },
    { type: 'checkListItem', content: [{ type: 'text', text: 'Follow up after meeting' }], props: { checked: false } },
  ],
  task: [
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'To-do', styles: {} }] },
    { type: 'checkListItem', content: [{ type: 'text', text: 'First task' }], props: { checked: false } },
    { type: 'checkListItem', content: [{ type: 'text', text: 'Second task' }], props: { checked: false } },
    { type: 'checkListItem', content: [{ type: 'text', text: 'Third task' }], props: { checked: false } },
  ],
  doc: [
    { type: 'heading', props: { level: 1 }, content: [{ type: 'text', text: 'Documentation', styles: {} }] },
    { type: 'paragraph', content: [{ type: 'text', text: 'Start writing your technical documentation here. Use the toolbar to add headings, lists, code blocks, and more.' }] },
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Overview', styles: {} }] },
    { type: 'paragraph', content: [] },
    { type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Usage', styles: {} }] },
    { type: 'codeBlock', content: [{ type: 'text', text: '// Example code' }], props: { language: 'javascript' } },
  ],
};

/**
 * Seed a new page with template content. If no template is given
 * (or unknown), the page starts empty.
 */
export function seedPageContent(pageId: string, templateKey: string): void {
  const content = TEMPLATE_CONTENT[templateKey];
  if (!content || content.length === 0) return;
  savePageContent(pageId, content);
}

/**
 * Save page content (remote, debounced)
 */
export function savePageContent(pageId: string, blocks: any[]): void {
  try {
    const key = CONTENT_KEY_PREFIX + pageId;
    // F 145 B — Block[] stays CANONICAL and markdown is persisted alongside it
    // as a derived, disposable artifact.
    //
    // The dispatch asked for the reverse. That was measured and refused: the
    // serialiser flattens nested lists, loses ordered-list numbering and
    // destroys table structure, so storing markdown as the source of truth
    // would corrupt a document on its first edit. See pageContentFormat.ts.
    //
    // What §4 actually needed is delivered anyway: markdown is now written with
    // every page, so a raw-markdown toggle can read source text instead of
    // re-serialising blocks. That was F 132's blocker, and it is resolved.
    RemoteState.set(key, JSON.stringify(writeContentEnvelope(blocks)));
  } catch (error) {
    console.error('[ContentStorage] Failed to save content for page:', pageId, error);
  }
}

/**
 * Load page content (cache, hydrated from the server)
 */
export function loadPageContent(pageId: string): any[] | null {
  try {
    const key = CONTENT_KEY_PREFIX + pageId;
    const data = RemoteState.get(key);
    if (!data) return null;
    // readContentEnvelope returns the full envelope; callers want the blocks.
    return readContentEnvelope(data)?.blocks ?? null;
  } catch (error) {
    console.error('[ContentStorage] Failed to load content for page:', pageId, error);
  }
  return null;
}

/**
 * The canonical markdown for a page, or null if it has never been written.
 *
 * F 145 B — this is the whole point of the migration. A consumer that wants
 * lossless source text reads THIS, not a re-serialisation of the blocks.
 */
export function loadPageMarkdown(pageId: string): string | null {
  try {
    const data = RemoteState.get(CONTENT_KEY_PREFIX + pageId);
    if (!data) return null;
    const env = readContentEnvelope(data);
    return env?.md ?? null;
  } catch {
    return null;
  }
}

/** Drops recorded at the time of the last write, if any. */
export function loadPageDrops(pageId: string): DropReason[] {
  try {
    const data = RemoteState.get(CONTENT_KEY_PREFIX + pageId);
    if (!data) return [];
    const parsed = JSON.parse(data);
    if (Array.isArray(parsed)) return [];           // legacy Block[]-only
    return (parsed?.drops ?? []) as DropReason[];
  } catch {
    return [];
  }
}

/**
 * Delete page content (remote, debounced)
 */
export function deletePageContent(pageId: string): void {
  try {
    const key = CONTENT_KEY_PREFIX + pageId;
    RemoteState.remove(key);
  } catch (error) {
    console.error('[ContentStorage] Failed to delete content for page:', pageId, error);
  }
}

/**
 * Check if page has content
 */
export function hasPageContent(pageId: string): boolean {
  const key = CONTENT_KEY_PREFIX + pageId;
  return RemoteState.get(key) !== null;
}

/**
 * Get all page IDs that have content
 */
export function getAllPageContentIds(): string[] {
  // F 144 — this used to walk localStorage directly. After the migration that
  // is WRONG: on a fresh browser localStorage is empty while the server holds
  // every page's content, so the page tree would come back with no content at
  // all. The cache is the union of what the server sent and what is local, and
  // the localStorage mirror is seeded from it during hydration, so walking the
  // keys here covers both.
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const key of RemoteState.keys()) {
    if (key.startsWith(CONTENT_KEY_PREFIX)) {
      const pageId = key.substring(CONTENT_KEY_PREFIX.length);
      if (!seen.has(pageId)) { seen.add(pageId); ids.push(pageId); }
    }
  }
  return ids;
}

export default {
  savePageContent,
  loadPageContent,
  deletePageContent,
  hasPageContent,
  getAllPageContentIds
};